import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { Route as RouteIcon, X, Search, XCircle, AlertTriangle, Inbox, Loader2, Pencil, PlusCircle, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRouteEditor } from '../context/RouteEditorContext';
import { useBftRoutes } from '../context/BftRoutesContext';
import { useModals } from '../context/ModalContext';
import { usePermissions } from '../hooks/usePermissions';
import '../styles/routes-modal.css';
const ROUTES_PER_PAGE = 20;
const RoutesModal = ({ isOpen, onClose }) => {
    const { loadRoute, setIsEditing } = useRouteEditor();
    const { addRoute: addViewRoute, isRouteViewed } = useBftRoutes();
    const { openRouteEditorModal } = useModals();
    const { canModifyBft } = usePermissions();
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRoutes, setTotalRoutes] = useState(0);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(undefined);
    // Calculate pagination
    const totalPages = Math.ceil(totalRoutes / ROUTES_PER_PAGE);
    const hasNextPage = currentPage < totalPages;
    const hasPrevPage = currentPage > 1;
    // Fetch routes from server
    const fetchRoutes = useCallback(async (search = '', page = 1) => {
        setLoading(true);
        setError(null);
        try {
            // The API only returns JSON when there's a search parameter
            // Use a space character to get all routes (matches everything)
            const searchParam = search.trim() || ' ';
            const url = `/bft/?search=${encodeURIComponent(searchParam)}`;
            console.log('Fetching routes from:', url);
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch routes: ${response.statusText}`);
            }
            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                // API returned non-JSON (probably "OK" text)
                console.warn('API returned non-JSON response, likely no routes available');
                setRoutes([]);
                setTotalRoutes(0);
                setLoading(false);
                return;
            }
            const data = await response.json();
            if (data.result && Array.isArray(data.result)) {
                const sortedRoutes = data.result.sort((a, b) => a.our_ref.localeCompare(b.our_ref));
                // Client-side pagination since API doesn't support it
                const startIdx = (page - 1) * ROUTES_PER_PAGE;
                const endIdx = startIdx + ROUTES_PER_PAGE;
                const paginatedRoutes = sortedRoutes.slice(startIdx, endIdx);
                setRoutes(paginatedRoutes);
                setTotalRoutes(sortedRoutes.length);
            }
            else {
                setRoutes([]);
                setTotalRoutes(0);
            }
        }
        catch (err) {
            console.error('Error fetching routes:', err);
            setError(err instanceof Error ? err.message : 'Failed to load routes');
            setRoutes([]);
        }
        finally {
            setLoading(false);
        }
    }, []);
    // Load initial routes when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchRoutes('', 1);
            setSearchTerm('');
            setCurrentPage(1);
            // Focus search input after modal animation
            const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen, fetchRoutes]);
    // Handle search with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        // Set new timeout
        searchTimeoutRef.current = window.setTimeout(() => {
            setCurrentPage(1);
            fetchRoutes(value, 1);
        }, 300);
    };
    // Handle pagination
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        fetchRoutes(searchTerm, newPage);
    };
    // Handle route action
    const handleRouteAction = async (route, action) => {
        try {
            setActionLoading(`${action}-${route.our_ref}`);
            if (action === 'edit') {
                // Load the route into the editor for editing
                await loadRoute(route.our_ref);
                setIsEditing(true);
                openRouteEditorModal();
            }
            else {
                // Add to map as view-only (uses separate BftRoutesContext)
                await addViewRoute(route.our_ref);
            }
            // Close the modal after successful action
            onClose();
        }
        catch (err) {
            console.error(`Error ${action}ing route:`, err);
            setError(err instanceof Error ? err.message : `Failed to ${action} route`);
        }
        finally {
            setActionLoading(null);
        }
    };
    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "routes-modal-overlay", onClick: onClose, children: _jsxs("div", { className: "routes-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "routes-modal__header", children: [_jsxs("h2", { className: "routes-modal__title", children: [_jsx(RouteIcon, { size: 20, className: "routes-modal__title-icon" }), "Search BFT Routes"] }), _jsx("button", { className: "routes-modal__close", onClick: onClose, "aria-label": "Close", children: _jsx(X, { size: 24 }) })] }), _jsx("div", { className: "routes-modal__search", children: _jsxs("div", { className: "routes-modal__search-wrapper", children: [_jsx(Search, { size: 16, className: "routes-modal__search-icon" }), _jsx("input", { ref: searchInputRef, type: "text", className: "routes-modal__search-input", placeholder: "Search by reference or description...", value: searchTerm, onChange: handleSearchChange }), searchTerm && (_jsx("button", { className: "routes-modal__search-clear", onClick: () => {
                                    setSearchTerm('');
                                    setCurrentPage(1);
                                    fetchRoutes('', 1);
                                }, "aria-label": "Clear search", children: _jsx(XCircle, { size: 18 }) }))] }) }), _jsxs("div", { className: "routes-modal__body", children: [loading && (_jsxs("div", { className: "routes-modal__loading", children: [_jsx("div", { className: "routes-modal__spinner" }), _jsx("p", { children: "Loading BFT routes..." })] })), error && (_jsxs("div", { className: "routes-modal__error", children: [_jsx(AlertTriangle, { size: 48, className: "routes-modal__error-icon" }), _jsx("p", { children: error }), _jsx("button", { className: "routes-modal__retry-btn", onClick: () => fetchRoutes(searchTerm, currentPage), children: "Retry" })] })), !loading && !error && routes.length === 0 && (_jsxs("div", { className: "routes-modal__empty", children: [_jsx(Inbox, { size: 48, className: "routes-modal__empty-icon" }), _jsx("p", { children: searchTerm ? 'No BFT routes found matching your search' : 'No BFT routes available' })] })), !loading && !error && routes.length > 0 && (_jsx("div", { className: "routes-modal__table-wrapper", children: _jsxs("table", { className: "routes-modal__table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Reference" }), _jsx("th", { children: "Description" }), _jsx("th", { className: "routes-modal__actions-col", children: "Actions" })] }) }), _jsx("tbody", { children: routes.map((route) => (_jsxs("tr", { children: [_jsx("td", { className: "routes-modal__ref", children: route.our_ref }), _jsx("td", { className: "routes-modal__description", children: route.description || '—' }), _jsxs("td", { className: "routes-modal__actions", children: [canModifyBft && (_jsx("button", { className: "routes-modal__action-btn routes-modal__action-btn--edit", onClick: () => handleRouteAction(route, 'edit'), disabled: actionLoading !== null, title: "Edit route", children: actionLoading === `edit-${route.our_ref}` ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { size: 14, className: "routes-modal__spinner-icon" }), _jsx("span", { children: "Loading..." })] })) : (_jsxs(_Fragment, { children: [_jsx(Pencil, { size: 14 }), _jsx("span", { children: "Edit" })] })) })), _jsx("button", { className: `routes-modal__action-btn routes-modal__action-btn--add ${isRouteViewed(route.our_ref) ? 'routes-modal__action-btn--on-map' : ''}`, onClick: () => handleRouteAction(route, 'add'), disabled: actionLoading !== null || isRouteViewed(route.our_ref), title: isRouteViewed(route.our_ref) ? 'Already on map' : 'Add to map', children: actionLoading === `add-${route.our_ref}` ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { size: 14, className: "routes-modal__spinner-icon" }), _jsx("span", { children: "Adding..." })] })) : isRouteViewed(route.our_ref) ? (_jsxs(_Fragment, { children: [_jsx(Check, { size: 14 }), _jsx("span", { children: "On Map" })] })) : (_jsxs(_Fragment, { children: [_jsx(PlusCircle, { size: 14 }), _jsx("span", { children: "Add" })] })) })] })] }, route.our_ref))) })] }) }))] }), !loading && !error && totalRoutes > 0 && (_jsxs("div", { className: "routes-modal__footer", children: [_jsxs("div", { className: "routes-modal__pagination-info", children: ["Showing ", ((currentPage - 1) * ROUTES_PER_PAGE) + 1, " - ", Math.min(currentPage * ROUTES_PER_PAGE, totalRoutes), " of ", totalRoutes, " BFT routes"] }), totalPages > 1 && (_jsxs("div", { className: "routes-modal__pagination", children: [_jsx("button", { className: "routes-modal__page-btn", onClick: () => handlePageChange(currentPage - 1), disabled: !hasPrevPage, "aria-label": "Previous page", children: _jsx(ChevronLeft, { size: 16 }) }), _jsxs("div", { className: "routes-modal__page-numbers", children: [currentPage > 2 && (_jsxs(_Fragment, { children: [_jsx("button", { className: "routes-modal__page-number", onClick: () => handlePageChange(1), children: "1" }), currentPage > 3 && _jsx("span", { className: "routes-modal__page-ellipsis", children: "\u2026" })] })), hasPrevPage && (_jsx("button", { className: "routes-modal__page-number", onClick: () => handlePageChange(currentPage - 1), children: currentPage - 1 })), _jsx("button", { className: "routes-modal__page-number routes-modal__page-number--active", children: currentPage }), hasNextPage && (_jsx("button", { className: "routes-modal__page-number", onClick: () => handlePageChange(currentPage + 1), children: currentPage + 1 })), currentPage < totalPages - 1 && (_jsxs(_Fragment, { children: [currentPage < totalPages - 2 && _jsx("span", { className: "routes-modal__page-ellipsis", children: "\u2026" }), _jsx("button", { className: "routes-modal__page-number", onClick: () => handlePageChange(totalPages), children: totalPages })] }))] }), _jsx("button", { className: "routes-modal__page-btn", onClick: () => handlePageChange(currentPage + 1), disabled: !hasNextPage, "aria-label": "Next page", children: _jsx(ChevronRight, { size: 16 }) })] }))] }))] }) }));
};
export default RoutesModal;
